import React, { lazy, Suspense, useState } from 'react';
// prettier-ignore
import { Button, Drawer, Flex, Form, Input, Modal, Select, Table } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import Loading from 'components/loading/loading';
import { useTranslate, useCreate, useDelete } from '@refinedev/core';
import { useModal } from '@refinedev/antd';

import apiEndpoint from 'core/common/apiEndPoints';
import openNotificationWithIcon from 'core/utils/notification';
import constants from 'core/constants/Constants';
import usePermission from 'core/utils/usePermission';
import PrivateRouteConst from 'core/common/privateRouteConstant';
import { useTokenContext } from 'contexts/resource/ResourceContext';
// prettier-ignore
import { getRenderRuleDeleteButton, getRenderCopyButton } from 'pages/ruleChange/utils/getActionButton';
import RuleRow from 'pages/ruleChange/rule/RuleRow';

const RuleChangeCreate = lazy(() => import('pages/ruleChange/rule/RuleCreate'));
const renderGroupTable = (columns, group, rules, tableProps) => (
  <Table
    key={group.unique_id}
    rowKey="unique_id"
    {...tableProps}
    dataSource={rules?.map((rule) => ({
      ...rule,
      rule_group_type: group.rule_type,
      rule_group: group.unique_id,
    }))}
    columns={columns}
    size="small"
    showHeader={false}
    className="no-outer-border-table row-card-table"
    pagination={{
      ...tableProps?.pagination,
      size: 'default',
    }}
  />
);

// prettier-ignore
const RulePanel = ({ pageType, filterList, groups, refetch, children, appliedFilters, setAppliedFilters, ruleListRefetch }) => {
  const t = useTranslate();
  const { mutate: deleteItem } = useDelete();
  const { mutate: createCopy, isLoading: copyLoading } = useCreate();
    const checkPermission = usePermission();
  const { userDetails } = useTokenContext();
  const hasGridFilter = !!checkPermission(PrivateRouteConst.SHOW_GRID_FILTER);
  const ruleModal = useModal();
  const filterDrawer = useModal();
  const copyModal = useModal();
  const [activeRule, setActiveRule] = useState<{
    id: string;
    mode: string;
    groupId?: string;
  } | null>(null);
  const [copyRow, setCopyRow] = useState<any>(null);
  const [copyModalForm] = Form.useForm();
  const [filterForm] = Form.useForm();

  const deleteEntity = (resource, id) => {
    deleteItem(
      { resource, id, successNotification: false, errorNotification: false },
      {
        onSuccess: (data) => {
          const message = (data?.data as any)?.message || t('notifications.deleteSuccess');
          openNotificationWithIcon(constants.success, t('notifications.mutationSuccess'), message);
          refetch();
          Object.values(ruleListRefetch).forEach((fn: any) => fn?.());
        },
        onError: (error: any) => {
          const message = error?.message || t('notifications.deleteError');
          openNotificationWithIcon(constants.error, t('notifications.mutationError'), message);
        },
      }
    );
  };
  const applyFilters = () => {
    setAppliedFilters(filterForm.getFieldsValue());
    filterDrawer.close();
  };

  const resetFilters = () => {
    filterForm.resetFields();
    setAppliedFilters({});
    filterDrawer.close();
  };
  const ruleGroupListBasedOnType =
    groups?.filter((g) => g.rule_type === copyRow?.rule_group_type) || [];
  const handleCopyRule = async () => {
    try {
      const values = await copyModalForm.validateFields();
      createCopy(
        {
          resource: apiEndpoint.copyRule,
          values: {
            rule_name: values.rule_name,
            rule_unique_id: copyRow?.unique_id,
            rule_group_id: values.rule_group_id,
          },
          successNotification: false,
          errorNotification: false,
        },
        {
          onSuccess: () => {
            copyModal.close();
            copyModalForm.resetFields();
            setCopyRow(null);
            refetch();
          },
          onError: (error) => {
            const message = error?.message || t('notifications.somethingError');
            openNotificationWithIcon(
              constants.error,
              t('notifications.mutationError'),
              message
            );
          },
        }
      );
    } catch {
      // validation error, do nothing
    }
  };

  const renderRuleDeleteButton = getRenderRuleDeleteButton(
    deleteEntity,
    apiEndpoint,
    pageType,
    checkPermission,
    userDetails,
    t
  );
  const renderCopyButton = getRenderCopyButton(
    (rule) => {
      setCopyRow(rule);
      copyModalForm.resetFields();
      copyModalForm.setFieldsValue({ rule_name: '', rule_group_id: null });
      copyModal.show();
    },
    pageType,
    checkPermission
  );

  const ruleColumns = [
    {
      dataIndex: 'rule_name',
      render: (_, rule) => (
        <RuleRow
          rule={rule}
          pageType={pageType}
          t={t}
          onView={(r) => {
            setActiveRule({ id: r.unique_id, mode: constants.pageType.show });
            ruleModal.show();
          }}
          onEdit={(r) => {
            setActiveRule({ id: r.unique_id, mode: constants.pageType.edit, groupId: r.rule_group });
            ruleModal.show();
          }}
          renderCopyButton={renderCopyButton}
          renderRuleDeleteButton={renderRuleDeleteButton}
        />
      ),
    },
  ];
  const filterButton = (
    <Button type="primary" className="my-6 ml-1 secondary-btn"
      icon={<FilterOutlined />} onClick={() => filterDrawer.show()}>
      {t('rulePlans.messages.filter')}
    </Button>
  );
  return (
    <>
      {children({
        columns: {
          render: (group, rules, serverPage) =>
            renderGroupTable(ruleColumns, group, rules, serverPage),
        },
        appliedFilters,
        filterButton: hasGridFilter ? filterButton : null,
      })}
      <Drawer
        title={
          activeRule?.mode === constants.pageType.show
            ? t('rules.ruleDetails')
            : `${t('actions.edit')} ${t('rules.ruleDetails')}`
        }
        width="85%"
        open={ruleModal.modalProps.open}
        onClose={() => ruleModal.close()}
        destroyOnClose
        maskClosable={false}
      >
        <Suspense fallback={<Loading />}>
          <RuleChangeCreate
            key={`${activeRule?.id}-${activeRule?.mode}`}
            type={activeRule?.mode}
            id={activeRule?.id}
            pageType={pageType}
            onDone={() => {
              ruleModal.close();
              refetch();
              ruleListRefetch[activeRule?.groupId as string]?.();
            }}
          />
        </Suspense>
      </Drawer>
      <Drawer
        title={t('rulePlans.messages.filter')}
        width="70%"
        open={filterDrawer.modalProps.open}
        maskClosable={false}

        onClose={() => filterDrawer.close()}
        footer={
          <Flex justify="end" gap={8}>
            <Button onClick={resetFilters}>{t('buttons.reset')}</Button>
            <Button type="primary" onClick={applyFilters}>
              {t('buttons.submit')}
            </Button>
          </Flex>
        }
      >
        <Form form={filterForm} layout="vertical">
          <Form.Item name="name" label={t('rules.ruleName')}>
            <Input size="large" placeholder={t('rules.searchRuleName')} />
          </Form.Item>
          <Form.Item name="status" label={t('common.status')}>
            <Select
              mode="multiple"
              options={[
                { label: t('rules.ruleActive'), value: 'Active' },
                { label: t('rules.ruleInactive'), value: 'Inactive' },
              ]}
            />
          </Form.Item>
          {filterList?.map((filter) => (
            <Form.Item
              key={filter.filter_name}
              name={['dynamic', filter.filter_name]}
              label={filter.filter_name}
            >
              <Select
                mode="multiple"
                options={filter.filter_list.map((o) => ({
                  label: o.name.replaceAll(' ', ''),
                  value: o.value,
                }))}
              />
            </Form.Item>
          ))}
        </Form>
      </Drawer>

      <Modal
        title={t('rulePlans.copyRulePlanModalTitle')}
        open={copyModal.modalProps.open}
        onOk={handleCopyRule}
        onCancel={() => copyModal.close()}
        okButtonProps={{ loading: copyLoading }}
        cancelButtonProps={{ disabled: copyLoading }}
        closable={!copyLoading}
        maskClosable={false}
      >
        <Form form={copyModalForm} layout="vertical">
          <Form.Item
            name="rule_name"
            label={t('rules.ruleName')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="rule_group_id"
            label={t('ruleGroup.ruleGroupTitle')}
            rules={[{ required: true }]}
          >
            <Select
              options={ruleGroupListBasedOnType?.map((g: any) => ({
                value: g.unique_id,
                label: g.rule_group_name,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RulePanel;
